import { Geometry, Mesh, Shader, State } from 'pixi.js';
import type { RarityMaterial } from './palette';

/**
 * Blockari's chest (apps/client/src/render/chest-mesh.ts), ported exactly —
 * the same geometry, projection, hinge, lights, specular strengths and rim —
 * and dressed in 21 Wild's materials: felt where Blockari has painted planks,
 * dark wood inside, a felt lining under the lid, a spade on the clasp where
 * Blockari has a jewel, and poker chips where Blockari heaps coins. The
 * colours come from ./palette.ts.
 *
 * Blockari's own description follows.
 *
 * The chest: a real 3D mesh, lit per fragment in the tiles' material
 * language (render/tile-material.ts) — a fixed key from the top-left, an
 * ambient floor, a Blinn specular with a tight "gem" glint, a rim light in
 * the rarity's colour — with a lid on a hinge.
 *
 * Geometry is built once in object space (the chest is 1 unit wide, its
 * base on y = 0, the front toward +z) from rounded boxes and a barrel lid,
 * and projected in the vertex shader with a small perspective camera, so
 * the lid swings through real depth. Pixi's canvas has a depth buffer; the
 * mesh depth-tests against itself (the screen draws nothing else in 3D).
 *
 * Materials by vertex: 0 body planks, 1 metal bands, 2 dark trim (the
 * inside walls), 3 the emissive interior, 4 the lock plate, 5 the lock's
 * jewel. Part 0 is the body, part 1 the lid.
 */

/** Hinge line of the lid (y, z) and the lid's barrel. */
const BODY_H = 0.46;
const DEPTH = 0.31;
const HINGE_Y = BODY_H;
const HINGE_Z = -DEPTH;
/** Where the chest pivots for squash and sway: the middle of its base. */
const PIVOT_Y = 0.0;
/** The screen-space anchor sits this far up the chest (its visual centre). */
export const CHEST_CENTRE_Y = 0.36;

const VERT = /* glsl */ `
in vec3 aPos;
in vec3 aNormal;
in vec2 aMat;
out vec3 vN;
out vec3 vObj;
out vec3 vView;
out float vMat;
out float vLid;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform mat3 uRot;
uniform vec3 uSquash;
uniform float uLid;
uniform vec2 uCenter;
uniform float uSize;
uniform float uDist;
uniform float uLift;

void main() {
  vec3 p = aPos;
  vec3 n = aNormal;
  if (aMat.y > 0.5) {
    // The lid swings back about the hinge: front edge up and over.
    vec3 h = vec3(0.0, ${HINGE_Y.toFixed(3)}, ${HINGE_Z.toFixed(3)});
    vec3 q = p - h;
    float c = cos(uLid);
    float s = sin(uLid);
    p = vec3(q.x, q.y * c + q.z * s, -q.y * s + q.z * c) + h;
    n = vec3(n.x, n.y * c + n.z * s, -n.y * s + n.z * c);
  }
  vObj = aPos;
  // Squash and stretch from the base, then the model/camera rotation about the chest's centre.
  p.y -= ${PIVOT_Y.toFixed(3)};
  p *= uSquash;
  n = normalize(n / uSquash);
  p.y += ${PIVOT_Y.toFixed(3)} + uLift;
  vec3 c0 = vec3(0.0, ${CHEST_CENTRE_Y.toFixed(3)}, 0.0);
  p = uRot * (p - c0);
  n = uRot * n;
  float k = uDist / (uDist - p.z);
  vec2 sp = uCenter + vec2(p.x, -p.y) * uSize * k;
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(sp, 1.0)).xy, clamp(-p.z * 0.3, -0.99, 0.99), 1.0);
  vN = n;
  vView = normalize(vec3(0.0, 0.0, uDist) - p);
  vMat = aMat.x;
  vLid = aMat.y;
}
`;

const FRAG = /* glsl */ `
precision highp float;
in vec3 vN;
in vec3 vObj;
in vec3 vView;
in float vMat;
in float vLid;
out vec4 finalColor;

uniform vec3 uBody;
uniform vec3 uBodyDark;
uniform vec3 uMetal;
uniform vec3 uMetalDark;
uniform vec3 uGem;
uniform vec3 uGlow;
uniform vec3 uEmit;
uniform float uEmitK;
uniform float uFlash;
uniform float uRimK;
uniform float uGlint;

const vec3 KEY = normalize(vec3(-0.5, 0.8, 0.62));
const vec3 FILL = normalize(vec3(0.8, 0.1, 0.5));

// A plank groove: 0 in the groove, 1 on the plank; d is the distance to the nearest seam in units.
float groove(float d, float w) {
  return smoothstep(w * 0.4, w, d);
}

float hash2(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}
float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash2(i), hash2(i + vec2(1.0, 0.0)), f.x),
             mix(hash2(i + vec2(0.0, 1.0)), hash2(i + vec2(1.0, 1.0)), f.x), f.y);
}
// Felt: a soft, low-frequency mottle in the face's own plane (a clean cloth, no sparkle).
float fibre(vec3 o, vec3 n) {
  vec3 a = abs(n);
  vec2 uv = a.y > 0.6 ? o.xz : (a.x > a.z ? o.zy : o.xy);
  return noise2(uv * 18.0) * 0.6 + noise2(uv * 41.0 + 7.0) * 0.4;
}
// iq's exact triangle distance.
float sdTri(vec2 p, vec2 p0, vec2 p1, vec2 p2) {
  vec2 e0 = p1 - p0, e1 = p2 - p1, e2 = p0 - p2;
  vec2 v0 = p - p0, v1 = p - p1, v2 = p - p2;
  vec2 pq0 = v0 - e0 * clamp(dot(v0, e0) / dot(e0, e0), 0.0, 1.0);
  vec2 pq1 = v1 - e1 * clamp(dot(v1, e1) / dot(e1, e1), 0.0, 1.0);
  vec2 pq2 = v2 - e2 * clamp(dot(v2, e2) / dot(e2, e2), 0.0, 1.0);
  float s = sign(e0.x * e2.y - e0.y * e2.x);
  vec2 d = min(min(vec2(dot(pq0, pq0), s * (v0.x * e0.y - v0.y * e0.x)),
                   vec2(dot(pq1, pq1), s * (v1.x * e1.y - v1.y * e1.x))),
                   vec2(dot(pq2, pq2), s * (v2.x * e2.y - v2.y * e2.x)));
  return -sqrt(d.x) * sign(d.y);
}
// The spade, point up, in a unit box (negative inside).
float sdSpade(vec2 q) {
  float lobes = min(length(q - vec2(-0.27, -0.12)) - 0.3, length(q - vec2(0.27, -0.12)) - 0.3);
  float top = sdTri(q, vec2(0.0, 0.66), vec2(-0.55, -0.02), vec2(0.55, -0.02));
  float stem = sdTri(q, vec2(0.0, -0.12), vec2(-0.24, -0.66), vec2(0.24, -0.66));
  return min(min(lobes, top), stem);
}

void main() {
  vec3 n = normalize(vN);
  vec3 V = normalize(vView);
  // A face seen from behind (the inside of the lid's barrel): turn it round and shade it as inside.
  bool inside = dot(n, V) < -0.02;
  if (inside) n = -n;
  int m = int(vMat + 0.5);

  vec3 base;
  float specK;
  float specPow;
  float emit = 0.0;
  if (m == 0) {
    // Felt (Blockari: painted planks): fibrous and matte, a shade deeper at
    // the foot, with the velvet catch at grazing angles below.
    float fb = fibre(vObj, n);
    float foot = smoothstep(0.02, 0.4, vObj.y);
    base = mix(uBodyDark, uBody, 0.62 + 0.38 * foot) * (0.96 + 0.08 * fb);
    specK = 0.06;
    specPow = 10.0;
  } else if (m == 1 || m == 4) {
    // Metal: a banded gold, rivets on the straps' faces.
    float bands = 0.5 + 0.5 * n.y;
    base = mix(uMetalDark, uMetal, 0.55 + 0.45 * bands);
    vec2 rv = vec2(abs(vObj.x) - 0.3, (fract(vObj.y / 0.13) - 0.5) * 0.13);
    float riv = 1.0 - smoothstep(0.012, 0.02, length(rv));
    if (m == 1 && abs(n.z) > 0.6 && abs(abs(vObj.x) - 0.3) < 0.05) {
      base = mix(base, mix(uMetal, vec3(1.0), 0.45), riv);
      n = normalize(n + vec3(rv / 0.02 * 0.6 * riv, 0.0));
    }
    if (m == 4) {
      // The keyhole: a dark slot under the jewel.
      vec2 kp = vec2(vObj.x, vObj.y - 0.285);
      float key = 1.0 - smoothstep(0.012, 0.016, max(abs(kp.x) * 1.6, abs(kp.y) - 0.022));
      base = mix(base, uMetalDark * 0.25, key * step(0.3, n.z));
    }
    // The lid's frame and straps catch the key light more than the body's bands; 10% down brings them to the same gold.
    // Its specular is a quarter down too: the lid faces the key light, and the highlight read lemon-pale on it.
    specK = (m == 1 && vLid > 0.5) ? 0.64 : 0.85;
    if (m == 1 && vLid > 0.5) base *= 0.9;
    specPow = 48.0;
  } else if (m == 2) {
    // The inside walls: dark wood, its grain running along.
    float grain = noise2(vec2(vObj.x * 4.0, vObj.y * 90.0 + vObj.z * 90.0));
    base = mix(vec3(0.16, 0.09, 0.05), vec3(0.3, 0.18, 0.1), grain);
    specK = 0.08;
    specPow = 16.0;
  } else if (m == 7) {
    // The lid's inside: a felt lining with faint, large boards (no noise), warmed by the treasure's light.
    float lf = fract(vObj.x / 0.125);
    float lg = groove(min(lf, 1.0 - lf) * 0.125, 0.01);
    base = mix(uBodyDark * 0.24, uBodyDark * 0.4, lg) + uEmit * 0.06 * uEmitK;
    specK = 0.05;
    specPow = 12.0;
  } else if (m == 6) {
    // Chips (Blockari: coins): overlapping discs in the table's colours, each
    // with the edge spots of a casino chip and a bright face.
    vec2 q = vObj.xz * vec2(13.0, 13.0);
    vec2 cell = floor(q + 0.5 * vec2(0.0, mod(floor(q.x), 2.0)));
    vec2 f = fract(q + 0.5 * vec2(0.0, mod(floor(q.x), 2.0))) - 0.5;
    float r = length(f);
    float h = fract(sin(dot(cell, vec2(12.9898, 78.233))) * 43758.5453);
    vec3 chip = h < 0.2 ? vec3(0.96, 0.25, 0.37)
      : h < 0.4 ? vec3(0.2, 0.83, 0.6)
      : h < 0.6 ? vec3(0.26, 0.38, 0.93)
      : h < 0.8 ? vec3(0.08, 0.07, 0.1)
      : vec3(1.0, 0.77, 0.24);
    float ring = smoothstep(0.3, 0.33, r) * (1.0 - smoothstep(0.43, 0.47, r));
    float spots = step(0.5, fract(atan(f.y, f.x) / 6.2832 * 6.0 + h));
    float inlay = smoothstep(0.2, 0.22, r) * (1.0 - smoothstep(0.24, 0.26, r));
    base = mix(chip, vec3(0.97, 0.95, 0.9), max(ring * spots, inlay * 0.7));
    base *= 1.0 - 0.35 * smoothstep(0.44, 0.5, r);
    n = normalize(n + vec3(f * 0.9 * (1.0 - ring), 0.0).xzy * 0.6);
    specK = 0.5;
    specPow = 40.0;
    emit = 0.3 * uEmitK;
  } else if (m == 3) {
    base = uEmit;
    emit = uEmitK;
    specK = 0.0;
    specPow = 8.0;
  } else {
    // The clasp (Blockari: a jewel): gold, with a spade in black enamel on its face.
    float bands = 0.5 + 0.5 * n.y;
    base = mix(uMetalDark, uMetal, 0.55 + 0.45 * bands);
    if (n.z > 0.3) {
      vec2 q = vec2(vObj.x, vObj.y - ${(BODY_H + 0.012 + 0.035).toFixed(3)}) / 0.03;
      float d = sdSpade(q);
      float aa = 0.05;
      base = mix(base, uGem + 0.03, 1.0 - smoothstep(-aa, aa, d));
    }
    specK = 0.85;
    specPow = 48.0;
  }

  // Inside faces (the lid's underside once it is up) sit in shadow.
  if (inside && m != 3) base *= 0.42;
  float diff = max(dot(n, KEY), 0.0);
  float fill = max(dot(n, FILL), 0.0);
  vec3 H = normalize(KEY + V);
  float nh = max(dot(n, H), 0.0);
  float spec = pow(nh, specPow) * specK;
  // The gem glint: a tight white pip, like the tiles'.
  float glint = pow(nh, 420.0) * uGlint * (m == 5 || m == 1 ? 1.0 : 0.35);
  float fres = pow(1.0 - max(dot(n, V), 0.0), 2.6);

  vec3 col = base * (0.5 + 0.62 * diff + 0.16 * fill);
  col += vec3(spec + glint);
  col += uGlow * fres * uRimK;
  // Felt: the velvet catch where the surface turns away.
  if (m == 0) col += mix(base, vec3(1.0), 0.35) * pow(1.0 - max(dot(n, V), 0.0), 2.2) * 0.22;
  // A soft contact darkening toward the base.
  col *= mix(0.62, 1.0, smoothstep(0.0, 0.12, vObj.y));
  if (emit > 0.0) col = mix(col, base * (1.0 + emit), clamp(emit, 0.0, 1.0));
  col = mix(col, vec3(1.0, 0.97, 0.9), uFlash);
  finalColor = vec4(col, 1.0);
}
`;

/* --------------------------------------------------------------------------
   Geometry
   -------------------------------------------------------------------------- */

class Builder {
  readonly pos: number[] = [];
  readonly nrm: number[] = [];
  readonly mat: number[] = [];
  readonly idx: number[] = [];

  vert(p: [number, number, number], n: [number, number, number], m: number, part: number): number {
    this.pos.push(p[0], p[1], p[2]);
    this.nrm.push(n[0], n[1], n[2]);
    this.mat.push(m, part);
    return this.pos.length / 3 - 1;
  }

  /**
   * A rounded box: a cube's six faces on a grid whose ticks crowd the edges,
   * each point pulled onto the rounded surface (clamp to the inner box, then
   * out by `r` along the offset), with that offset as the normal.
   */
  roundedBox(
    c: [number, number, number],
    h: [number, number, number],
    r: number,
    m: number,
    part: number,
  ): void {
    // Grid ticks crowding the rounded edges; `project` bends them onto the arc.
    const ticks = (hh: number) => {
      const inner = Math.max(0, hh - r);
      return [-hh, -hh + r * 0.1, -hh + r * 0.38, -inner, inner, hh - r * 0.38, hh - r * 0.1, hh];
    };
    const tx = ticks(h[0]);
    const ty = ticks(h[1]);
    const tz = ticks(h[2]);
    const project = (
      p: [number, number, number],
    ): { p: [number, number, number]; n: [number, number, number] } => {
      const q: [number, number, number] = [
        Math.max(-(h[0] - r), Math.min(h[0] - r, p[0])),
        Math.max(-(h[1] - r), Math.min(h[1] - r, p[1])),
        Math.max(-(h[2] - r), Math.min(h[2] - r, p[2])),
      ];
      const d: [number, number, number] = [p[0] - q[0], p[1] - q[1], p[2] - q[2]];
      const len = Math.hypot(d[0], d[1], d[2]) || 1;
      const n: [number, number, number] = [d[0] / len, d[1] / len, d[2] / len];
      return { p: [c[0] + q[0] + n[0] * r, c[1] + q[1] + n[1] * r, c[2] + q[2] + n[2] * r], n };
    };
    // Each face: axis `a` fixed at sign s, grid over the other two (u, v), wound outward.
    const faces: { a: 0 | 1 | 2; s: 1 | -1 }[] = [
      { a: 0, s: 1 },
      { a: 0, s: -1 },
      { a: 1, s: 1 },
      { a: 1, s: -1 },
      { a: 2, s: 1 },
      { a: 2, s: -1 },
    ];
    const T = [tx, ty, tz];
    for (const f of faces) {
      const ua = ((f.a + 1) % 3) as 0 | 1 | 2;
      const va = ((f.a + 2) % 3) as 0 | 1 | 2;
      const us = T[ua]!;
      const vs = T[va]!;
      const base = this.pos.length / 3;
      for (const vv of vs)
        for (const uu of us) {
          const p: [number, number, number] = [0, 0, 0];
          p[f.a] = f.s * h[f.a];
          p[ua] = uu;
          p[va] = vv;
          const out = project(p);
          this.vert(out.p, out.n, m, part);
        }
      const W = us.length;
      for (let j = 0; j < vs.length - 1; j++)
        for (let i = 0; i < W - 1; i++) {
          const a = base + j * W + i;
          const b = a + 1;
          const cc = a + W;
          const d = cc + 1;
          if (f.s > 0) this.idx.push(a, b, d, a, d, cc);
          else this.idx.push(a, d, b, a, cc, d);
        }
    }
  }

  /**
   * The barrel lid: a half-cylinder along x from `x0` to `x1`, its axis at
   * (y0, 0), radius `rz` in depth and `ry` in height, with end caps.
   */
  barrel(
    x0: number,
    x1: number,
    y0: number,
    rz: number,
    ry: number,
    m: number,
    part: number,
    caps = true,
  ): void {
    const N = 20;
    const base = this.pos.length / 3;
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * Math.PI; // 0 = front (+z), π = back
      const z = Math.cos(a) * rz;
      const y = y0 + Math.sin(a) * ry;
      // The ellipse's normal.
      const nz = Math.cos(a) / rz;
      const ny = Math.sin(a) / ry;
      const l = Math.hypot(nz, ny);
      this.vert([x0, y, z], [0, ny / l, nz / l], m, part);
      this.vert([x1, y, z], [0, ny / l, nz / l], m, part);
    }
    for (let i = 0; i < N; i++) {
      const a = base + i * 2;
      this.idx.push(a, a + 1, a + 3, a, a + 3, a + 2);
    }
    if (!caps) return;
    for (const [x, s] of [
      [x0, -1],
      [x1, 1],
    ] as const) {
      const c = this.vert([x, y0, 0], [s, 0, 0], m, part);
      const ring: number[] = [];
      for (let i = 0; i <= N; i++) {
        const a = (i / N) * Math.PI;
        ring.push(this.vert([x, y0 + Math.sin(a) * ry, Math.cos(a) * rz], [s, 0, 0], m, part));
      }
      for (let i = 0; i < N; i++)
        if (s > 0) this.idx.push(c, ring[i]!, ring[i + 1]!);
        else this.idx.push(c, ring[i + 1]!, ring[i]!);
    }
  }

  /** A downward-facing quad (the lid's inner panel). */
  quadDown(
    x0: number,
    x1: number,
    y: number,
    z0: number,
    z1: number,
    m: number,
    part: number,
  ): void {
    const a = this.vert([x0, y, z0], [0, -1, 0], m, part);
    const b = this.vert([x1, y, z0], [0, -1, 0], m, part);
    const c = this.vert([x1, y, z1], [0, -1, 0], m, part);
    const d = this.vert([x0, y, z1], [0, -1, 0], m, part);
    this.idx.push(a, c, b, a, d, c);
  }

  /** The upper half of an ellipsoid (the treasure heaped in the chest). */
  dome(c: [number, number, number], r: [number, number, number], m: number, part: number): void {
    const NU = 22;
    const NV = 8;
    const base = this.pos.length / 3;
    for (let j = 0; j <= NV; j++) {
      const v = (j / NV) * (Math.PI / 2);
      for (let i = 0; i <= NU; i++) {
        const u = (i / NU) * Math.PI * 2;
        const x = Math.cos(v) * Math.cos(u);
        const z = Math.cos(v) * Math.sin(u);
        const y = Math.sin(v);
        const n: [number, number, number] = [x / r[0], y / r[1], z / r[2]];
        const l = Math.hypot(n[0], n[1], n[2]) || 1;
        this.vert(
          [c[0] + x * r[0], c[1] + y * r[1], c[2] + z * r[2]],
          [n[0] / l, n[1] / l, n[2] / l],
          m,
          part,
        );
      }
    }
    for (let j = 0; j < NV; j++)
      for (let i = 0; i < NU; i++) {
        const a = base + j * (NU + 1) + i;
        const b = a + NU + 1;
        this.idx.push(a, b, a + 1, a + 1, b, b + 1);
      }
  }

  /** An upward-facing quad (the lit interior). */
  quadUp(x0: number, x1: number, y: number, z0: number, z1: number, m: number, part: number): void {
    const a = this.vert([x0, y, z0], [0, 1, 0], m, part);
    const b = this.vert([x1, y, z0], [0, 1, 0], m, part);
    const c = this.vert([x1, y, z1], [0, 1, 0], m, part);
    const d = this.vert([x0, y, z1], [0, 1, 0], m, part);
    this.idx.push(a, b, c, a, c, d);
  }
}

function buildChest(): Builder {
  const b = new Builder();
  const W = 0.5;
  const D = DEPTH;
  // Body.
  b.roundedBox([0, BODY_H / 2, 0], [W, BODY_H / 2, D], 0.045, 0, 0);
  // The lit interior just under the rim, and dark inner walls (visible once the lid is up).
  b.quadUp(-W + 0.03, W - 0.03, BODY_H + 0.012, -D + 0.03, D - 0.03, 3, 0);
  // The treasure: a heap of coins, lit from inside once the lid is up.
  b.dome([0, BODY_H + 0.012, 0.01], [W - 0.07, 0.1, D - 0.07], 6, 0);
  // Base trim, top rim, corner posts, vertical straps.
  b.roundedBox([0, 0.035, 0], [W + 0.022, 0.04, D + 0.022], 0.02, 1, 0);
  b.roundedBox([0, BODY_H - 0.02, 0], [W + 0.018, 0.03, D + 0.018], 0.018, 1, 0);
  for (const x of [-1, 1])
    for (const z of [-1, 1])
      b.roundedBox(
        [x * (W - 0.012), BODY_H / 2, z * (D - 0.012)],
        [0.04, BODY_H / 2 + 0.004, 0.04],
        0.016,
        1,
        0,
      );
  for (const x of [-0.3, 0.3])
    b.roundedBox([x, BODY_H / 2, 0], [0.055, BODY_H / 2 + 0.002, D + 0.012], 0.018, 1, 0);
  // Lock plate and its jewel.
  b.roundedBox([0, BODY_H - 0.12, D + 0.02], [0.085, 0.1, 0.022], 0.018, 4, 0);
  // Lid (part 1): the barrel, its rim, two straps over it, the hasp.
  const LY = BODY_H + 0.012;
  // The lid's rim: a frame of four bars (open, it shows the dark panel inside it).
  const RB = 0.045;
  b.roundedBox([0, LY + 0.02, D + 0.018 - RB], [W + 0.018, 0.032, RB], 0.018, 1, 1);
  b.roundedBox([0, LY + 0.02, -D - 0.018 + RB], [W + 0.018, 0.032, RB], 0.018, 1, 1);
  for (const x of [-1, 1])
    b.roundedBox([x * (W + 0.018 - RB), LY + 0.02, 0], [RB, 0.032, D + 0.018], 0.018, 1, 1);
  b.barrel(-W + 0.004, W - 0.004, LY + 0.04, D - 0.004, 0.235, 0, 1);
  for (const x of [-0.3, 0.3])
    b.barrel(x - 0.055, x + 0.055, LY + 0.04, D + 0.014, 0.25, 1, 1, false);
  // The lid's inside: a dark panel (what the open lid shows).
  b.quadDown(-W + 0.02, W - 0.02, LY - 0.006, -D + 0.02, D - 0.02, 7, 1);
  b.roundedBox([0, LY + 0.04, D + 0.03], [0.062, 0.06, 0.02], 0.016, 1, 1);
  b.roundedBox([0, LY + 0.035, D + 0.052], [0.036, 0.036, 0.016], 0.014, 5, 1);
  return b;
}

/* --------------------------------------------------------------------------
   The mesh
   -------------------------------------------------------------------------- */

function rgb(c: number): [number, number, number] {
  return [((c >> 16) & 0xff) / 255, ((c >> 8) & 0xff) / 255, (c & 0xff) / 255];
}

export interface ChestPose {
  /** Screen centre of the chest's visual middle, px. */
  x: number;
  y: number;
  /** Chest width on screen, px. */
  size: number;
  /** Yaw (turn), pitch (camera looking down), roll (wobble), radians. */
  yaw: number;
  pitch: number;
  roll: number;
  /** Squash: x/z and y scale (1 = rest). */
  sx: number;
  sy: number;
  /** Lift above the resting base, chest units. */
  lift: number;
  /** Lid angle, radians (0 shut). */
  lid: number;
  /** White flash 0..1, rim light strength, emissive interior strength. */
  flash: number;
  rim: number;
  emit: number;
}

export class ChestMesh {
  readonly mesh: Mesh<Geometry, Shader>;
  private readonly shader: Shader;

  constructor() {
    const b = buildChest();
    const geometry = new Geometry({
      attributes: {
        aPos: { buffer: new Float32Array(b.pos), format: 'float32x3' },
        aNormal: { buffer: new Float32Array(b.nrm), format: 'float32x3' },
        aMat: { buffer: new Float32Array(b.mat), format: 'float32x2' },
      },
      indexBuffer: new Uint32Array(b.idx),
    });
    this.shader = Shader.from({
      gl: { vertex: VERT, fragment: FRAG, name: 'w21-chest' },
      resources: {
        chestUniforms: {
          uRot: { value: new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]), type: 'mat3x3<f32>' },
          uSquash: { value: [1, 1, 1], type: 'vec3<f32>' },
          uLid: { value: 0, type: 'f32' },
          uCenter: { value: [0, 0], type: 'vec2<f32>' },
          uSize: { value: 100, type: 'f32' },
          uDist: { value: 3.4, type: 'f32' },
          uLift: { value: 0, type: 'f32' },
          uBody: { value: [1, 1, 1], type: 'vec3<f32>' },
          uBodyDark: { value: [0.5, 0.5, 0.5], type: 'vec3<f32>' },
          uMetal: { value: [1, 0.8, 0.3], type: 'vec3<f32>' },
          uMetalDark: { value: [0.6, 0.4, 0.1], type: 'vec3<f32>' },
          uGem: { value: [1, 0.3, 0.5], type: 'vec3<f32>' },
          uGlow: { value: [0.4, 0.9, 0.8], type: 'vec3<f32>' },
          uEmit: { value: [1, 0.93, 0.75], type: 'vec3<f32>' },
          uEmitK: { value: 0, type: 'f32' },
          uFlash: { value: 0, type: 'f32' },
          uRimK: { value: 0.5, type: 'f32' },
          uGlint: { value: 0.9, type: 'f32' },
        },
      },
    });
    const state = State.for2d();
    state.depthTest = true;
    state.depthMask = true;
    state.blend = false;
    this.mesh = new Mesh({ geometry, shader: this.shader, state });
  }

  private get u(): Record<string, unknown> {
    return (this.shader.resources as { chestUniforms: { uniforms: Record<string, unknown> } })
      .chestUniforms.uniforms;
  }

  /** The rarity's materials; `t` blends from `from` (0) to `to` (1) for the recolour. */
  setMaterial(from: RarityMaterial, to: RarityMaterial, t: number): void {
    const mix = (a: number, b: number): [number, number, number] => {
      const x = rgb(a);
      const y = rgb(b);
      return [x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t];
    };
    const u = this.u;
    u['uBody'] = mix(from.body, to.body);
    u['uBodyDark'] = mix(from.bodyDark, to.bodyDark);
    u['uMetal'] = mix(from.metal, to.metal);
    u['uMetalDark'] = mix(from.metalDark, to.metalDark);
    u['uGem'] = mix(from.gem, to.gem);
    u['uGlow'] = mix(from.glow, to.glow);
    const e = mix(from.glow, to.glow);
    u['uEmit'] = [0.75 + e[0] * 0.25, 0.7 + e[1] * 0.3, 0.55 + e[2] * 0.3];
  }

  setPose(p: ChestPose): void {
    const u = this.u;
    // R = Rx(pitch) · Ry(yaw) · Rz(roll), column-major for GLSL.
    const cx = Math.cos(p.pitch);
    const sx = Math.sin(p.pitch);
    const cy = Math.cos(p.yaw);
    const sy = Math.sin(p.yaw);
    const cz = Math.cos(p.roll);
    const sz = Math.sin(p.roll);
    const Rz = [
      [cz, -sz, 0],
      [sz, cz, 0],
      [0, 0, 1],
    ];
    const Ry = [
      [cy, 0, sy],
      [0, 1, 0],
      [-sy, 0, cy],
    ];
    const Rx = [
      [1, 0, 0],
      [0, cx, -sx],
      [0, sx, cx],
    ];
    const mul = (A: number[][], B: number[][]) =>
      A.map((row) =>
        [0, 1, 2].map((j) => row[0]! * B[0]![j]! + row[1]! * B[1]![j]! + row[2]! * B[2]![j]!),
      );
    const R = mul(Rx, mul(Ry, Rz));
    const m = u['uRot'] as Float32Array;
    for (let col = 0; col < 3; col++)
      for (let row = 0; row < 3; row++) m[col * 3 + row] = R[row]![col]!;
    u['uRot'] = m;
    u['uSquash'] = [p.sx, p.sy, p.sx];
    u['uLid'] = p.lid;
    u['uCenter'] = [p.x, p.y];
    u['uSize'] = p.size;
    u['uLift'] = p.lift;
    u['uFlash'] = p.flash;
    u['uRimK'] = p.rim;
    u['uEmitK'] = p.emit;
  }
}

/** Where a chest-space point lands on screen for a pose (the cards leave from the chest's mouth). */
export function projectChestPoint(
  p: ChestPose,
  x: number,
  y: number,
  z: number,
): { x: number; y: number } {
  const cx = Math.cos(p.pitch);
  const sx = Math.sin(p.pitch);
  const cy = Math.cos(p.yaw);
  const sy = Math.sin(p.yaw);
  let px = x * p.sx;
  let py = y * p.sy + p.lift - CHEST_CENTRE_Y;
  let pz = z * p.sx;
  // Ry then Rx (roll ignored: it is a small wobble).
  const x1 = cy * px + sy * pz;
  const z1 = -sy * px + cy * pz;
  px = x1;
  pz = z1;
  const y2 = cx * py - sx * pz;
  const z2 = sx * py + cx * pz;
  py = y2;
  pz = z2;
  const k = 3.4 / (3.4 - pz);
  return { x: p.x + px * p.size * k, y: p.y - py * p.size * k };
}
